<template>
  <div class="school-form">
    <el-form
      :label-position="labelPosition"
      :label-width="labelWidthForm"
      :model="model"
      :rules="rules"
      @submit.native.prevent="doSubmit"
      class="form"
      ref="form"
    >
      <!-- Subtitle -->
      <h2 class="app-content-subtitle">
        <app-i18n code="entities.school.form.subtitle.information" />
      </h2>

      <el-row v-if="model" :gutter="20" class="school-form__row">
        <!-- Name -->
        <el-col :lg="11" :md="16" :sm="24" class="school-form__row__col">
          <el-form-item
            :label="fields.name.label"
            :prop="fields.name.name"
            class="school-form__row__col__item"
          >
            <!-- Input -->
            <el-input
              v-if="showInputName || isEditing"
              v-model="model[fields.name.name]"
              prefix-icon="icon-home"
              :placeholder="i18n('entities.school.placeholders.emptySchool')"
              :disabled="disabledInput"
            />
            <!-- Select -->
            <el-select
              v-else
              v-model="schoolSelectValue"
              :disabled="schoolsMapLoading"
              :loading="schoolsMapLoading"              
              :placeholder="i18n('entities.school.placeholders.fillSchool')"
              filterable
              @visible-change="onChangeVisibleChangeSelect"
              class="school-form__row__col__item__select"
              ref="select"
            >
              <!-- Select Empty -->
              <div
                slot="empty"
                class="school-form__row__col__item__select__empty"
                @click="handleAddNewSchool"
              >
                <i class="school-form__row__col__item__select__empty__icon icon-add" slot="prefix" />
                <span
                  class="school-form__row__col__item__select__empty__text"
                  v-text="i18n('entities.school.new.title')"
                />
              </div>          
              <!-- Prefix -->
              <i slot="prefix" class="icon-home" />
              <!-- Options -->
              <el-option
                class="school-form__row__col__item__select__option"
                v-for="item in schoolOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              >
                <span
                  class="school-form__row__col__item__select__option__content"
                  v-text="item.label"
                />
              </el-option> 
            </el-select>
          </el-form-item>
        </el-col>

        <!-- Location -->
        <el-col :lg="11" :md="16" :sm="24">
          <el-form-item
            :label="fields.location.label" 
            :prop="fields.location.name"
          >
            <app-location-input
              :key="locationKey"
              v-if="model"
              :disabled="disabledLocation"
              :placeholder="
                i18n('entities.school.placeholders.location')
              "
              v-model="model.location"
              ref="input"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <!-- Separator -->
      <div v-if="!existSchool || isEditing" class="app-content-separator" />

      <!-- Subtitle -->
      <h2 v-if="!existSchool || isEditing" class="app-content-subtitle">
        <app-i18n code="entities.school.form.subtitle.management" />
      </h2>

      <!-- Description -->
      <h3 v-if="!existSchool || isEditing" class="app-content-description">
        <app-i18n code="entities.school.form.description.email" />
      </h3>

      <el-row v-if="model" :gutter="10">
        <!-- Emails -->
        <el-col :lg="24" :md="24" :sm="24">
          <el-form-item
            v-if="!existSchool || isEditing"
            :label="fields.emails.label"
            :prop="fields.emails.name"
            :required="fields.emails.required"
          >
            <emails-input
              v-model="model[fields.emails.name]"
              :key="model.id"
              :disabled="disabledLocation"
              prefix-icon="icon-mail"
              :placeholder="
                i18n('entities.school.placeholders.email')
              "
            />
          </el-form-item>
        </el-col>
      </el-row>

      <!-- Form Controls -->
      <el-form-item class="school-form__controls">
        <div class="form-buttons">
          <el-button type="secondary" @click="doBackFn">
            <app-i18n code="common.back.default" />
          </el-button>

          <el-button class="right" :disabled="saveLoading" @click="doSubmit" type="primary">
            <app-i18n v-if="!isEditing" code="common.match" />
            <app-i18n v-if="isEditing" code="common.save" />
          </el-button>
        </div>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { FormSchema } from '@shared/form/form-schema';
import { SchoolModel } from '@modules/school/school-model';
import { i18n } from '@shared/i18n';
import { get, update } from 'lodash';

const { fields } = SchoolModel;

const formSchema = new FormSchema([
  fields.id,
  fields.name,
  fields.location,
  fields.emails,
]);

export default {
  name: 'app-school-form',

  props: {
    id: {
      type: String,
    },
    doBackFn: {
      type: Function,
      required: true,
    },
    onSubmitFn: {
      type: Function,
    },
  },

  data() {
    return {
      rules: formSchema.rules(),
      model: null,
      schoolSelectValue: '',
      showInputName: false,
      isSelectOpened: false,
      locationKey: 0,
      disabledInput: false,
    };
  },

  computed: {
    ...mapGetters({
      labelPosition: 'layout/labelPosition',
      labelWidthForm: 'layout/labelWidthForm',
      record: 'school/form/record',
      findLoading: 'school/form/findLoading',
      saveLoading: 'school/form/saveLoading',
      schoolsMap: 'school/form/schoolsMap',
      schoolsMapLoading: 'school/form/schoolsMapLoading',
      currentManage: 'auth/manage/currentManage',
    }),

    isEmptySchools({ schoolsMap }) {
      return Object.keys(schoolsMap).length === 0
    },

    subpropertyId() {
      return get(
        this,
        'currentManage.subproperty.id',
        null,
      );
    },

    schoolOptions() {
      if (!this.schoolsMap) return [];

      return Object.values(
        this.schoolsMap,
      ).map(({ name, id }) => ({ label: name, value: id }));
    },

    existSchool() {
      return (
        this.model &&
        this.model.id &&
        this.model.id !== null
      );
    },

    isEditing() {
      return !!this.id;
    },

    fields() {
      return fields;
    },

    sizeOptions() {
      return this.schoolOptions
        ? this.schoolOptions.length
        : 0;
    },

    disabledLocation() {
      return !(!this.existSchool || this.isEditing);
    },
  },

  async mounted() {
    if (this.isEditing) {
      await this.doFind({
        id: this.id,
        subpropertyId: this.subpropertyId,
      });
    } else {
      this.doNew();
    }
    this.model = formSchema.initialValues(this.record);

    await this.doLoadSchools({
      subpropertyId: this.subpropertyId,
    });

    if (this.isEmptySchools === true) this.showInputName = true 
  },

  methods: {
    ...mapActions({
      doLoadSchools: 'school/form/doLoadSchools',
      doFind: 'school/form/doFind',
      doNew: 'school/form/doNew',
      doUpdate: 'school/form/doUpdate',
      doCreate: 'school/form/doCreate',
    }),

    doReset() {
      this.model = formSchema.initialValues(this.record);
    },

    async doSubmit() {
      try {
        await this.$refs.form.validate();
      } catch (error) {
        return;
      }

      const { id, ...values } = formSchema.cast(this.model);

      if (this.isEditing) {
        return this.onSubmitFn({
          id,
          ...values,
        });
      } else {
        return this.onSubmitFn({
          id,
          ...values,
        });
      }
    },

    i18n(param) {
      return i18n(param);
    },

    async handleAddNewSchool() {
      this.showInputName = true;
      update(
        this,
        'model.name',
        () => this.$refs.select.query,
      );
      update(this, 'model.id', () => null);
      update(this, 'model.location', () => null);
      update(this, 'model.emails', () => []);
      update(this, 'schoolSelectValue', () => null);
      
      // Validate name field
      this.$nextTick(() => {
        this.$refs.form.validateField('name', () => {});
      });

      // Reset select open
      this.isSelectOpened = false;

      // Update location input
      this.locationKey++
    },

    onChangeVisibleChangeSelect() {
      this.isSelectOpened = !this.isSelectOpened;
    },

    setIconSelectEmpty() {
      this.$nextTick(() => {
        update(
          this,
          '$refs.select.$refs.reference.$el.children[2].innerHTML',
          () => '',
        );
      });
    },

    setIconSelectUp() {
      this.$nextTick(() => {
        update(
          this,
          '$refs.select.$refs.reference.$el.children[2].innerHTML',
          () =>
            '<i class="el-select__caret el-input__icon el-icon-arrow-up"></i>',
        );
      });
    },

    setIconSelectDown() {
      this.$nextTick(() => {
        update(
          this,
          '$refs.select.$refs.reference.$el.children[2].innerHTML',
          () =>
            '<i class="el-select__caret el-input__icon el-icon-arrow-up is-reverse"></i>',
        );
      });
    },
  },

  watch: {
    schoolSelectValue(val) {
      if (val) {
        const school = get(this, `schoolsMap.${val}`, [])
        this.model = school
        this.locationKey++ 
      }
    },
    async 'model.name'(name) {
      if (this.isEmptySchools === true) {
        this.disabledInput = false
      } else {
        if (name === '') {
          this.disabledInput = true
          await this.doLoadSchools({
            subpropertyId: this.subpropertyId,
          })
          this.showInputName = false        
        } else {
          this.disabledInput = false
        }
      }

    },
    sizeOptions(sizeOptions) {
      if (sizeOptions > 0) {
        if (this.isSelectOpened) {
          this.setIconSelectDown();
        } else {
          this.setIconSelectUp();
        }
      } else {
        this.setIconSelectEmpty();
      }
    },
    isSelectOpened(isOpen) {
      if (this.sizeOptions > 0) {
        if (isOpen) {
          this.setIconSelectDown();
        } else {
          this.setIconSelectUp();
        }
      } else {
        this.setIconSelectEmpty();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.school-form {
  &__row {
    padding-top: 32px;
    &__col {
      &__item {
        &__select {
          &__option {
            padding: 0 0 0 0;
            padding-left: 40px;
            &__content {
              color: #6b6d72;
              font-size: 16px;
            }
          }
          &__empty {
            display: grid;
            grid-template-columns: 24px auto;
            grid-template-rows: auto;
            grid-template-areas: 'icon text';
            color: #6b6d72;
            background: #f8f8f8;
            padding: 16px;
            padding-left: 40px;
            cursor: pointer;

            &__icon {
              grid-area: icon;
              color: #c5c5c5;
            }
            &__text {
              grid-area: text;
              font-size: 14px;
              text-transform: uppercase;
            }
          }
        }
      }
    }
  }
  &__controls {
    padding-top: 32px;
  }
}
.app-content-separator {
  margin: 25px 0 40px 0;
}
.app-content-subtitle {
  margin: 0px;
  margin-top: 30px;
}
.app-content-description {
  margin-top: 16px;
  margin-bottom: 30px;
}
</style>
